// SyncBar.tsx
import { Cached } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import useFlagBagController from "hooks/useFlagBagController";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { dateFormatter } from "services/dateHelper";
import { useData } from "store/DataProvider";
import { useSync } from "store/SyncContext";
import { AppStatus, useAppStatus } from "hooks/useAppStatus";
import { PendingDataInfo, SyncStatus, usePendingQueue, } from "hooks/usePendingQueue";
import { useEffect, useRef, useState } from "react";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";

interface SyncBarProps {
	handleSubmit: (data: any) => void;
	pendingSyncCount?: number;
	pendingProcessCount?: number;
}

const SyncBar: React.FC<SyncBarProps> = ({ handleSubmit, pendingSyncCount, pendingProcessCount }) => {
	const theme = useTheme();
	const data = useData();
	const { isSyncing, setIsSyncing, lastUpdateDate, setLastUpdateDate } = useSync();
	const { status, pendingCount, refreshAppStatus, isOnline } = useAppStatus();
	const { setSyncStatus, getPendingDataInfo } = usePendingQueue();
	const FlagBagController = useFlagBagController();
	const [pendingQueue, setPendingQueue] = useState<PendingDataInfo>();
	const db = initIndexedDbIfMissingStore("records");
	const currentPendingSyncCount = pendingSyncCount ?? pendingQueue?.pendingCount ?? pendingCount ?? 0;
	const currentPendingProcessCount = pendingProcessCount ?? pendingQueue?.failedCount ?? 0;

	const handleSync = async () => {
		if (!data.getStationId()) {
			enqueueSnackbar("請選擇旗站", { variant: "error" });
			return;
		}
		if (isSyncing) {
			enqueueSnackbar("請等待同步", { variant: "info" });
			return;
		}

		setIsSyncing(true);
		setSyncStatus(SyncStatus.SYNCING);

		try {
			const syncResult = await FlagBagController.flushOfflineData();

			try {
				const eventId = data.getEventId();
				if (eventId) {
					const { stationApi } = await import('services/api');
					const overview = await stationApi.getOverview(eventId);
					data.setStationOverviewData(overview);
				}
			} catch (overviewError) {
				console.warn("Failed to sync station overview:", overviewError);
			}

			// 初始化数据库（updateFlagBagAction 内部会自动更新 lastUpdateDate）
			const result = await FlagBagController.initDB(data.getStationId());
			handleSubmit(result);

			// 根据同步结果设置状态和显示消息
			if (syncResult && syncResult.hasErrors) {
				// 有错误
				if (syncResult.totalSuccess > 0) {
					// 部分成功
					setSyncStatus(SyncStatus.PARTIAL_SUCCESS);
					enqueueSnackbar(
						`部分同步成功：${syncResult.totalSuccess} 條成功，${syncResult.totalFailed} 條失敗`, 
						{ variant: "warning", autoHideDuration: 4000 }
					);
				} else {
					// 全部失败
					setSyncStatus(SyncStatus.FAILED);
					enqueueSnackbar(
						`同步失敗：${syncResult.totalFailed} 條數據同步失敗`, 
						{ variant: "error", autoHideDuration: 4000 }
					);
				}
			} else {
				// 完全成功
				setSyncStatus(SyncStatus.SUCCESS);
				if (syncResult && syncResult.totalSuccess > 0) {
					enqueueSnackbar(
						`同步成功：${syncResult.totalSuccess} 條數據已同步`, 
						{ variant: "success", autoHideDuration: 3000 }
					);
				} else {
					enqueueSnackbar("數據已同步", { variant: "success", autoHideDuration: 3000 });
				}
			}
		} catch (error) {
			// 同步失败
			setSyncStatus(SyncStatus.FAILED);
			enqueueSnackbar("同步失敗", { variant: "error" });
		} finally {
			setIsSyncing(false);
			// 刷新应用状态
			setTimeout(() => {
				refreshAppStatus();
			}, 200);
		}
	};

	const handleSyncRef = useRef(handleSync);
	handleSyncRef.current = handleSync;
	const prevOnlineRef = useRef<boolean | null>(null);

	useEffect(() => {
		const wasOffline = prevOnlineRef.current === false;
		prevOnlineRef.current = isOnline;
		if (wasOffline && isOnline) {
			if (!process.env.REACT_APP_SYNC_ON_CONNECT || process.env.REACT_APP_SYNC_ON_CONNECT !== "false") {
				void handleSyncRef.current();
			}
		}
	}, [isOnline]);

	const getText = () => {
		switch (status) {
			case AppStatus.ONLINE_SYNCED:
				return "已同步";
			case AppStatus.ONLINE_PENDING:
				return `${currentPendingSyncCount} 條待同步`;
			case AppStatus.ONLINE_SYNCING:
				return `同步中（${pendingQueue?.syncedCount}/${pendingQueue?.totalCount}）`;
			case AppStatus.OFFLINE_PENDING:
				return `${currentPendingSyncCount} 條數據待同步`;
			case AppStatus.ONLINE_FAILED:
				return `同步出錯（${currentPendingProcessCount}條數據待處理）`;
			default:
				return "";
		}
	}

	const getColor = () => {
		switch (status) {
			case AppStatus.ONLINE_SYNCED:
				return theme.palette.online_status.online;  // 绿色
			case AppStatus.ONLINE_PENDING:
			case AppStatus.OFFLINE_PENDING:
				return theme.palette.online_status.offline; // 橙色
			case AppStatus.ONLINE_SYNCING:
				return theme.palette.info.main;             // 蓝色
			case AppStatus.ONLINE_FAILED:
				return theme.palette.error.main;            // 红色
			default:
				return theme.palette.online_status.offline;
		}
	};

	useEffect(() => {
		getPendingDataInfo().then((data) => {
			setPendingQueue(data);
		});
	}, [status, getPendingDataInfo]);

	// Initialize last update date from database on mount
	useEffect(() => {
		const loadLastUpdateDate = async () => {
			const stationId = data.getStationId();
			if (!stationId || lastUpdateDate) return;

			try {
				const cachedData = await db.getByID(stationId);
				if (cachedData?.LastUpdateTimeLocalTime) {
					const lastDate = new Date(cachedData.LastUpdateTimeLocalTime);
					setLastUpdateDate(lastDate);
				}
			} catch (error) {
				console.error("Failed to restore last update date:", error);
			}
		};

		loadLastUpdateDate();
	}, [data, lastUpdateDate, db, setLastUpdateDate]);

	return (
		<Box
			width={1}
			px={2}
			boxShadow={2}
			position={"fixed"}
			alignItems={"center"}
			justifyContent={"space-between"}
			display="flex"
			flexDirection={"row"}
			onClick={() => {
				// 只有在同步中时不能点击，其他情况都可以点击同步
				if (status === AppStatus.ONLINE_SYNCING) return;
				// 离线时不能点击
				if (status === AppStatus.OFFLINE_PENDING) return;
				handleSync();
			}}
			zIndex={1000}
			sx={{ 
				backgroundColor: getColor(),
				cursor: (status === AppStatus.ONLINE_SYNCING || status === AppStatus.OFFLINE_PENDING) 
					? 'not-allowed' 
					: 'pointer',
				transition: 'all 0.3s ease',
				'&:hover': (status !== AppStatus.ONLINE_SYNCING && status !== AppStatus.OFFLINE_PENDING) && {
					opacity: 0.9,
					boxShadow: 3
				},
				minHeight: '20px'
			}}>
			<Box
				display="flex"
				flexDirection={"row"}
				gap={1}
				alignItems={"center"}
				sx={{
					color: "#fff",
					flex: 1,
					minWidth: 0
				}}>
				<Box
					className={isSyncing ? "spin" : ""}
					display="flex"
					alignItems={"center"}
					sx={{ flexShrink: 0 }}
				>
					<Cached sx={{ width: 20, height: 20 }} />
				</Box>
				<Typography
					fontWeight="600"
					variant={"body2"}
					sx={{ 
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis'
					}}>
					{getText()}
				</Typography>
			</Box>
			<Box 
				sx={{ 
					color: "#fff",
					flexShrink: 0,
					ml: 2,
					display: { xs: 'none', sm: 'block' }
				}}>
				<Typography 
					variant={"body2"}
					sx={{ whiteSpace: 'nowrap' }}>
					{"最後同步於 " + (lastUpdateDate ? dateFormatter(lastUpdateDate) : "尚未同步")}
				</Typography>
			</Box>
			<Box 
				sx={{ 
					color: "#fff",
					flexShrink: 0,
					ml: 1,
					display: { xs: 'block', sm: 'none' }
				}}>
				<Typography 
					variant={"caption"}
					sx={{ whiteSpace: 'nowrap' }}>
					{lastUpdateDate ? dateFormatter(lastUpdateDate) : "未同步"}
				</Typography>
			</Box>
		</Box>
	);
};

export default React.memo(SyncBar);
